import React, {
    createRef,
    PureComponent,
    ReactNode,
    KeyboardEvent as ReactKeyboardEvent
} from 'react';
import cx from 'classnames';
import {noop} from '@priority-software/priority-style-react';
import throttle from 'lodash.throttle';
import {TableCell} from './parts/TableCell';
import {Row} from './parts/TableRow';
import {
    generateRowOffsets,
    handleNavigation,
    isEqualArraysShallow,
    isValidIndex,
    withinRange
} from './utils';
import {CONST} from './constants';
import {ColumnsStyles, ColumnType, OpenTableMethods} from './types';
import {Direction, Scroller} from './parts/Scroller';
import {HeaderCell} from './parts/HeaderCell';
import {validatePosition} from './utils/validatePosition';
import {createDataTestId} from '../../../../utils/dataTestId';
import {Record} from '../../../../stores/Form/Record';
import styles from './styles/Table.module.scss';

type Props = {
    /*Row height */
    rowHeight?: number;
    /*Header height */
    headerHeight?: number;
    /* Custom `id` attached to Table element */
    id?: string;
    /* Custom component CSS classes */
    componentClasses?: string;
    /* Header renderer */
    headerRenderer: (column: ColumnType, pinned: boolean) => ReactNode;
    cellRenderer: <T>(params: {
        rowData: T | null;
        column: ColumnType;
        isActiveCell: boolean;
        rowIndex: number;
    }) => ReactNode;
    columns: ColumnType[];
    data: Array<Record>;
    searchModeLevel: number;
    activeCellIndex: number;
    activeRowIndex: number;
    rowsCount: number;
    rowClasses?: (rowIndex: number, isActiveRow?: boolean, isFocused?: boolean) => string;
    onCellClick?: (id: number, rowIndex: number) => void;
    isRtl?: boolean;
    onFocusCell?: (cellIndex: number, rowIndex: number) => void;
    isActive?: boolean;
    fixedColumnsNumber: number;
    onReady?: (tableAPI: OpenTableMethods) => void;
    highlightCurrentRow?: boolean;
    validateFocusChange?: (
        cell: number,
        row: number,
        action: (cellIndex: number, currentRow: number) => void
    ) => void;
    blockedNavigation?: boolean;
    cellClasses?: string;
    rounded?: boolean;
    onTableBodyClick?: () => void;
    onCellResizeEnd?: (width: number, columnId: number) => void;
    onScroll?: (leftOffset: number, topOffset: number) => void;
    defaultScrollOffsetLeft?: number;
    defaultScrollOffsetTop?: number;
    onDestroy?: () => void;
    onKeyDown?: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
};

type State = {
    viewportWidth: number;
    viewportHeight: number;
    columnIndexes: Array<number>;
    rowIndexes: Array<number>;
    columnsStyles: ColumnsStyles;
};

export class Table extends PureComponent<Props, State> {
    tableViewportRef = createRef<HTMLDivElement>();
    bodyRef = createRef<HTMLDivElement>();

    scrollbarWidth: number = 0;
    prevScrollOffsetTop: number = 0;
    prevScrollOffsetLeft: number = 0;
    rowOffsets: Array<number> = [];
    rowsPlaceholder = [];

    tableAPI: OpenTableMethods;
    resizeTimer: NodeJS.Timeout = null;
    resizeObserver: ResizeObserver;

    static defaultProps = {
        headerRenderer: noop,
        fixedColumnsNumber: 1,
        activeRowIndex: 0,
        activeCellIndex: 0
    };

    constructor(props) {
        super(props);

        this.state = {
            viewportWidth: 0,
            viewportHeight: 0,
            rowIndexes: [0, 0],
            columnIndexes: [0, 0],
            columnsStyles: this.getColumnStyles(props.columns)
        };

        this.tableAPI = {
            focusCell: this.focusCell,
            recalcColumnStyles: this.recalcColumnStyles,
            recalcGridDimensions: this.recalcGridDimensions
        };

        this.prevScrollOffsetTop = props.defaultScrollOffsetTop || 0;
        this.prevScrollOffsetLeft = props.defaultScrollOffsetLeft || 0;
    }

    get scrollableViewportWidth() {
        const viewportWidth =
            this.state.viewportWidth || this.tableViewportRef.current?.clientWidth;
        return viewportWidth - this.state.columnsStyles.totalFixedWidth || 0;
    }

    hasHorizontalScroll = () => {
        const {viewportWidth, columnsStyles} = this.state;
        const canvasWidth = columnsStyles.totalFloatedWidth + columnsStyles.totalFixedWidth;

        return viewportWidth < canvasWidth;
    };

    componentDidMount() {
        const viewportEl = this.tableViewportRef.current;
        const {onReady, activeRowIndex, activeCellIndex, isRtl} = this.props;
        const {columnsStyles} = this.state;
        const cellPosition = isRtl ? 'right' : 'left';
        const activeCellOffset = columnsStyles.cellStyles[activeCellIndex]?.[cellPosition] || 0;
        const activeRowOffset = this.rowOffsets[activeRowIndex] || 0;
        const viewportWidth = viewportEl.clientWidth;
        const viewportHeight = viewportEl.clientHeight;

        if (viewportEl) {
            this.setState({viewportWidth, viewportHeight}, () => {
                const startX = validatePosition(
                    this.prevScrollOffsetLeft,
                    viewportWidth,
                    activeCellOffset
                );
                const startY = validatePosition(
                    this.prevScrollOffsetTop,
                    viewportHeight,
                    activeRowOffset
                );
                this.scrollByCoordinates(startX, startY);
            });

            viewportEl.addEventListener('scroll', this.handleScroll);
            viewportEl.addEventListener(
                'wheel',
                (e: MouseEvent) => {
                    e.preventDefault();
                    return this.onMouseWheel(e);
                },
                {passive: false}
            );
        }

        window.addEventListener('keydown', this.handleKeydown);

        this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.recalcGridDimensions();
            }, CONST.RESIZE_TIMEOUT);
        });

        this.resizeObserver.observe(this.tableViewportRef.current);

        if (onReady) {
            onReady(this.tableAPI);
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        const viewportEl = this.tableViewportRef.current;
        if (!this.scrollbarWidth) {
            this.scrollbarWidth = viewportEl?.offsetWidth - viewportEl?.clientWidth || 0;
        }

        if (prevProps.fixedColumnsNumber !== this.props.fixedColumnsNumber) {
            this.recalcColumnStyles();
        }

        if (prevProps.activeRowIndex !== this.props.activeRowIndex) {
            this.scrollToRow(this.props.activeRowIndex);
        }

        if (prevProps.activeCellIndex !== this.props.activeCellIndex) {
            this.scrollToRow(this.props.activeCellIndex);
        }
    }

    scrollByCoordinates = (left?: number, top?: number) => {
        const viewportEl = this.tableViewportRef.current;

        if (typeof top === 'number' && viewportEl) {
            viewportEl.scrollTop = top;
        }

        if (typeof left === 'number' && viewportEl) {
            viewportEl.scrollLeft = left;
        }
    };

    onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.recalcGridDimensions(), CONST.RESIZE_TIMEOUT);
    };

    getColumnStyles = (columns: Array<ColumnType>) => {
        const {rowHeight, isRtl, fixedColumnsNumber} = this.props;
        const position = isRtl ? 'right' : 'left';

        return columns.reduce(
            (pr, col) => {
                const isFixed = col.index < fixedColumnsNumber;

                return {
                    ...pr,
                    cellStyles: {
                        ...pr.cellStyles,
                        [col.id]: {
                            width: col.width,
                            height: rowHeight,
                            [position]: isFixed ? pr.totalFixedWidth : pr.totalFloatedWidth
                        }
                    },
                    totalFloatedWidth: !isFixed
                        ? pr.totalFloatedWidth + col.width
                        : pr.totalFloatedWidth,
                    totalFixedWidth: isFixed ? pr.totalFixedWidth + col.width : pr.totalFixedWidth
                };
            },
            {totalFloatedWidth: 0, totalFixedWidth: 0, cellStyles: {}}
        );
    };

    recalcColumnStyles = () =>
        this.setState(() => {
            const columnsStyles = this.getColumnStyles(this.props.columns);
            const columnIndexes = this.calcCellIndexes(this.prevScrollOffsetLeft, columnsStyles);
            return {columnsStyles, columnIndexes};
        });

    recalcGridDimensions = () => {
        const {columnsStyles} = this.state;
        const viewportWidth = this.tableViewportRef.current?.clientWidth;
        const viewportHeight = this.tableViewportRef.current?.clientHeight;

        this.setState(
            {
                viewportWidth,
                viewportHeight
            },
            () => {
                this.setState({
                    columnIndexes: this.calcCellIndexes(this.prevScrollOffsetLeft, columnsStyles),
                    rowIndexes: this.calcRowIndex(
                        this.prevScrollOffsetTop,
                        viewportHeight,
                        this.props.rowHeight
                    )
                });
            }
        );
    };

    scrollToRow = idx => {
        const {headerHeight, rowHeight} = this.props;
        const {viewportHeight} = this.state;
        const lastLineOffset = this.prevScrollOffsetTop + viewportHeight - headerHeight - rowHeight;

        if (this.tableViewportRef.current) {
            if (this.rowOffsets[idx] < this.prevScrollOffsetTop) {
                // scroll to top edge of the viewport
                this.tableViewportRef.current.scrollTop = this.rowOffsets[idx];
            } else if (this.rowOffsets[idx] >= lastLineOffset) {
                // scroll to bottom edge of the viewport
                this.tableViewportRef.current.scrollTop =
                    this.rowOffsets[idx] + rowHeight + headerHeight - viewportHeight;
            }
        }
    };

    scrollToCell = idx => {
        const {isRtl, fixedColumnsNumber, columns} = this.props;
        const {columnsStyles} = this.state;
        const colId = columns[idx]?.id;
        const currCellStyles = columnsStyles.cellStyles[colId];

        if (colId && idx < fixedColumnsNumber) return;

        if (!isRtl) {
            if (currCellStyles.left < this.prevScrollOffsetLeft) {
                // LTR - handle sticky scroll left edge
                this.tableViewportRef.current.scrollLeft = currCellStyles.left;
            } else {
                this.tableViewportRef.current.scrollLeft =
                    currCellStyles.left -
                    this.scrollableViewportWidth +
                    currCellStyles.width +
                    this.scrollbarWidth;
                // LTR - handle sticky scroll right edge
            }
        } else {
            const lastCellOffset =
                this.prevScrollOffsetLeft + this.scrollableViewportWidth - currCellStyles.width;

            if (-currCellStyles.right < this.prevScrollOffsetLeft) {
                // RTL - handle sticky scroll right edge
                this.tableViewportRef.current.scrollLeft =
                    -currCellStyles.right + this.scrollableViewportWidth - currCellStyles.width;
            } else if (currCellStyles.right === 0 || -currCellStyles.right < lastCellOffset) {
                // RTL - handle sticky scroll left edge
                this.tableViewportRef.current.scrollLeft =
                    currCellStyles.right > 0 ? -currCellStyles.right : 0;
            }
        }
    };

    componentWillUnmount() {
        const viewportEl = this.tableViewportRef.current;

        if (viewportEl) {
            viewportEl.removeEventListener('scroll', this.handleScroll);
            viewportEl.removeEventListener('wheel', this.onMouseWheel);
        }

        window.removeEventListener('keydown', this.handleKeydown);

        this.resizeObserver.disconnect();
        this.props.onDestroy();
    }

    handleKeydown = (e: KeyboardEvent) => {
        const {
            columns,
            rowsCount,
            rowHeight,
            searchModeLevel,
            isActive,
            blockedNavigation,
            activeRowIndex,
            activeCellIndex
        } = this.props;
        const {viewportHeight} = this.state;

        if (isActive && !blockedNavigation) {
            handleNavigation(e, {
                columns,
                rowsCount,
                currentCellIndex: activeCellIndex,
                currentRow: activeRowIndex,
                setFocusCell: this.focusCell,
                screenRows: Math.round(viewportHeight / rowHeight) - CONST.ROWS_OVERSCAN,
                searchModeLevel
            });
        }
    };

    getVisibleColumns = (cols: Array<ColumnType>) => {
        const {columnIndexes} = this.state;
        const startIndex = Math.max(
            columnIndexes[0] - CONST.COLS_OVERSCAN,
            this.props.fixedColumnsNumber
        );
        const endIndex = Math.min(columnIndexes[1] + CONST.COLS_OVERSCAN, cols.length);

        return cols.slice(startIndex, endIndex);
    };

    renderHeader = (visibleColumns: Array<ColumnType>) => {
        const {headerRenderer, headerHeight, fixedColumnsNumber, onCellResizeEnd} = this.props;
        const {columnsStyles} = this.state;

        return (
            <div
                className={cx(styles.tableHeader, styles.tableRow)}
                style={{minHeight: headerHeight}}
                {...createDataTestId('table-header', false)}
            >
                {visibleColumns.map(col => {
                    const style = columnsStyles.cellStyles[col.id];
                    const pinned =
                        col.index === fixedColumnsNumber - 1 && this.hasHorizontalScroll();

                    return (
                        <HeaderCell
                            key={col.id}
                            column={col}
                            pinned={pinned}
                            onCellResizeEnd={onCellResizeEnd}
                            headerRenderer={headerRenderer}
                            componentClasses={styles.tableHeaderCell}
                            style={{...style, height: headerHeight}}
                        />
                    );
                })}
            </div>
        );
    };

    onCellClick = async (index: number, rowIndex: number) => {
        const {onCellClick} = this.props;
        await this.focusCell(index, rowIndex);

        if (onCellClick) {
            onCellClick(index, rowIndex);
        }
    };

    validateFocusChange = async (cellIndex: number, currentRow: number) => {
        if (this.props.validateFocusChange) {
            this.props.validateFocusChange(cellIndex, currentRow, this.changeCellFocus);
        } else {
            this.changeCellFocus(cellIndex, currentRow);
        }
    };

    changeCellFocus = (newCellIndex: number, newRowIndex: number) => {
        const {onFocusCell, activeRowIndex, activeCellIndex} = this.props;
        // do not trigger the state update if we set the same indexes
        if (newRowIndex !== activeRowIndex || newCellIndex !== activeCellIndex) {
            const {columnIndexes, rowIndexes} = this.state;
            if (!withinRange(newCellIndex, columnIndexes)) {
                this.scrollToCell(newCellIndex);
            }

            if (!withinRange(newRowIndex, rowIndexes, CONST.ROWS_OVERSCAN)) {
                this.scrollToRow(newRowIndex);
            }

            if (onFocusCell) {
                onFocusCell(newCellIndex, newRowIndex);
            }
        }
    };

    focusCell = async (newCellIndex: number, newRowIndex?: number) => {
        await this.validateFocusChange(newCellIndex, newRowIndex);
    };

    renderCells = (rowIndex: number, isRowActive: boolean, visibleColumns: Array<ColumnType>) => {
        const {
            cellRenderer,
            data,
            isActive: activeTable,
            activeCellIndex,
            cellClasses
        } = this.props;
        const {columnsStyles} = this.state;

        return visibleColumns.map(col => {
            const styles = columnsStyles.cellStyles[col.id];
            const isActiveCell = activeTable && isRowActive && activeCellIndex === col.index;

            return (
                <TableCell
                    key={col.id}
                    componentClasses={cellClasses}
                    isActive={isActiveCell}
                    columnStyles={styles}
                    column={col}
                    onClick={this.onCellClick}
                    rowIndex={rowIndex}
                    cellRenderer={cellRenderer}
                    rowData={data.length > 0 ? data[rowIndex] : undefined}
                />
            );
        });
    };

    setRowsOffset = (rowsCount: number) => {
        this.rowOffsets = generateRowOffsets(rowsCount, this.props.rowHeight);
    };

    calcRowIndex = (scrollOffset: number, viewportHeight: number, rowHeight) => {
        const startIndex = Math.max(Math.round(scrollOffset / rowHeight) - CONST.ROWS_OVERSCAN, 0);
        const endIndex = Math.round(
            (scrollOffset + viewportHeight - this.props.headerHeight) / rowHeight
        );

        return [startIndex, endIndex];
    };

    calcCellIndexes = (offset: number, columnsStyles: ColumnsStyles) => {
        const {cellStyles, totalFloatedWidth} = columnsStyles;
        const {columns, fixedColumnsNumber, isRtl} = this.props;
        const scrollOffset = Math.abs(offset);

        if (!cellStyles) return [0, 0];

        const columnsCount = columns.length;
        const position = isRtl ? 'right' : 'left';
        const values = Object.values(cellStyles);
        const lastFixedColumnIndex = fixedColumnsNumber - 1;

        const minIndex = values.findIndex((styles, idx) => {
            return idx > lastFixedColumnIndex && styles?.[position] >= scrollOffset;
        });

        const maxIndex = values.findIndex((styles, idx) => {
            return (
                idx >= minIndex &&
                styles?.[position] + styles?.width >=
                    scrollOffset + this.scrollableViewportWidth - this.scrollbarWidth
            );
        });

        const startIndex = isValidIndex(minIndex) ? minIndex : 0;
        const endIndex =
            isValidIndex(maxIndex) &&
            scrollOffset + this.scrollableViewportWidth < totalFloatedWidth
                ? maxIndex
                : columnsCount;
        return [startIndex, endIndex];
    };

    onMouseWheel = throttle(e => {
        if (this.props.isActive) {
            const delta = Math.round(e.deltaY);
            const currCellIndex = this.props.activeCellIndex;
            const currentRow = this.props.activeRowIndex;

            if (delta > 0) {
                const nextRowIndex = currentRow + 1;

                if (nextRowIndex < this.props.rowsCount)
                    this.focusCell(currCellIndex, nextRowIndex);
            } else if (delta < 0) {
                const nextRowIndex = currentRow - 1;

                if (nextRowIndex >= 0) this.focusCell(currCellIndex, nextRowIndex);
            }
        }
    }, 30);

    handleScroll = throttle(e => {
        const topOffset = e.target.scrollTop;
        const leftOffset = e.target.scrollLeft;

        let newIndexes = {};
        if (leftOffset !== this.prevScrollOffsetLeft) {
            // Horizontal scroll event
            const columnIndexes = this.calcCellIndexes(leftOffset, this.state.columnsStyles);

            this.prevScrollOffsetLeft = leftOffset;
            if (!isEqualArraysShallow(columnIndexes, this.state.columnIndexes)) {
                /* Update only new indexes */
                newIndexes = {...newIndexes, columnIndexes};
            }
        }

        if (topOffset !== this.prevScrollOffsetTop) {
            // Vertical scroll event
            this.prevScrollOffsetTop = topOffset;

            const rowIndexes = this.calcRowIndex(
                topOffset,
                this.state.viewportHeight,
                this.props.rowHeight
            );

            if (!isEqualArraysShallow(rowIndexes, this.state.rowIndexes)) {
                newIndexes = {...newIndexes, rowIndexes};
            }
        }

        /* Update only new indexes */
        if (!!Object.keys(newIndexes).length) {
            this.props.onScroll?.(leftOffset, topOffset);
            this.setState(newIndexes);
        }
    }, 40);

    getRowsToRender = () => {
        const {rowsCount} = this.props;

        if (rowsCount !== this.rowsPlaceholder.length) {
            // calc rows offsets
            this.setRowsOffset(rowsCount);
            this.rowsPlaceholder = new Array(rowsCount).fill(null);
        }

        return this.rowsPlaceholder;
    };

    getRowClasses = (rowIndex: number, isActive: boolean) =>
        this.props.rowClasses?.(rowIndex, isActive) || '';

    renderRecords = columns => {
        const {rowHeight, data, searchModeLevel} = this.props;
        const {rowIndexes} = this.state;

        return (
            <>
                {this.renderHeader(columns)}

                <div className={styles.tableBody} {...createDataTestId('table-body', false)}>
                    {this.getRowsToRender()
                        .slice(rowIndexes[0], rowIndexes[1])
                        .map((_, index) => {
                            const rowIndex = rowIndexes[0] + index;
                            const currentRow = rowIndex === this.props.activeRowIndex;
                            const rowKey =
                                data[rowIndex]?.clientKey || `${rowIndex}_${searchModeLevel}`;

                            return (
                                <Row
                                    key={rowKey}
                                    rowIndex={rowIndex}
                                    height={rowHeight}
                                    isActive={currentRow}
                                    componentClasses={this.getRowClasses(rowIndex, currentRow)}
                                    top={this.rowOffsets[rowIndex]}
                                >
                                    {this.renderCells(rowIndex, currentRow, columns)}
                                </Row>
                            );
                        })}
                </div>
            </>
        );
    };

    render() {
        const {
            id,
            componentClasses,
            rowHeight,
            rowsCount,
            columns,
            fixedColumnsNumber,
            headerHeight,
            isActive,
            highlightCurrentRow,
            isRtl,
            onTableBodyClick,
            onKeyDown,
            rounded
        } = this.props;
        const {columnsStyles} = this.state;
        const fixedColumns = columns.slice(0, fixedColumnsNumber);
        const canvasWidth = columnsStyles.totalFloatedWidth + columnsStyles.totalFixedWidth;
        const canvasHeight = headerHeight + rowsCount * rowHeight;
        const visibleColumns = this.getVisibleColumns(columns);

        return (
            <div
                onKeyDown={onKeyDown}
                className={cx(styles.component, componentClasses, {[styles.rounded]: rounded})}
                {...createDataTestId('form-grid-view-container', false)}
            >
                <div
                    className={cx(styles.virtualizedTable, {
                        [styles.highlightCurrentRow]: highlightCurrentRow || isActive
                    })}
                    id={id}
                    ref={this.tableViewportRef}
                >
                    <div className={styles.tableBodyBackdrop} onClick={onTableBodyClick} />
                    <div
                        className={styles.tableInner}
                        style={{height: canvasHeight, width: canvasWidth}}
                    >
                        <div className={styles.wrap}>
                            {fixedColumnsNumber > 0 && (
                                <div
                                    {...createDataTestId('fixed-column-table', false)}
                                    className={cx(styles.fixedPart, {
                                        [styles.shadow]: this.hasHorizontalScroll()
                                    })}
                                    style={{width: columnsStyles.totalFixedWidth}}
                                >
                                    {this.renderRecords(fixedColumns)}
                                </div>
                            )}

                            <div
                                className={styles.scrollableArea}
                                {...createDataTestId('scrollable-column-table', false)}
                            >
                                {this.renderRecords(visibleColumns)}
                            </div>
                        </div>
                    </div>
                </div>

                {canvasHeight > this.state.viewportHeight && (
                    <Scroller
                        isRtl={isRtl}
                        scrollableSize={canvasHeight}
                        componentClasses={cx(styles.tableScroll, styles.sideScroll)}
                        direction={Direction.vertical}
                        getAnchorEl={() => this.tableViewportRef.current}
                    />
                )}

                {canvasWidth > this.state.viewportWidth && (
                    <Scroller
                        componentClasses={cx(styles.tableScroll, styles.bottomScroll)}
                        isRtl={isRtl}
                        sidePadding={10}
                        scrollableSize={canvasWidth}
                        direction={Direction.horizontal}
                        getAnchorEl={() => this.tableViewportRef.current}
                    />
                )}
            </div>
        );
    }
}
